import type { ModelDocStatus } from '@rock/types';

import type { badgeVariants } from '@/components/ui/badge';
import type { VariantProps } from 'class-variance-authority';

type BadgeVariant = VariantProps<typeof badgeVariants>['variant'];

// The badge/filter presentation of `Model.docStatus` — the rollup itself is derived server-side by
// `deriveModelDocStatus` in @rock/manuals and stored on the model, so the list filters it with a real
// query. 'brand-review' is the one filter-only value left: it selects a pending `Model.brandSuggestion`
// rather than the documentation rollup, and is backed by its OWN complete server query (see
// `modelsNeedingBrandReviewAtom`).
export const DOC_STATUS_FILTERS = [
    'all',
    'missing',
    'searching',
    'needs-review',
    'published',
    'library-only',
    'search-failed',
    'ingest-failed',
    'revisit',
    'parked',
    'brand-review',
    'archived',
] as const;
export type DocStatusFilter = (typeof DOC_STATUS_FILTERS)[number];

interface DocStatusMeta {
    label: string;
    variant: BadgeVariant;
    className?: string;
}

export const DOC_STATUS_META: Record<ModelDocStatus, DocStatusMeta> = {
    missing: { label: 'Missing', variant: 'outline' },
    searching: {
        label: 'Searching',
        variant: 'secondary',
        className: 'bg-sky-100 text-sky-900 border-transparent',
    },
    'needs-review': {
        label: 'Needs review',
        variant: 'secondary',
        className: 'bg-amber-100 text-amber-900 border-transparent',
    },
    published: {
        label: 'Published',
        variant: 'secondary',
        className: 'bg-emerald-100 text-emerald-900 border-transparent',
    },
    'library-only': {
        label: 'Library only',
        variant: 'secondary',
        className: 'bg-slate-200 text-slate-700 border-transparent',
    },
    'search-failed': { label: 'Search failed', variant: 'destructive' },
    'ingest-failed': { label: 'Ingest failed', variant: 'destructive' },
    revisit: {
        label: 'Revisit',
        variant: 'secondary',
        className: 'bg-violet-100 text-violet-900 border-transparent',
    },
    parked: {
        label: 'Parked',
        variant: 'secondary',
        className: 'bg-slate-200 text-slate-700 border-transparent',
    },
    archived: {
        label: 'Archived',
        variant: 'secondary',
        className: 'bg-slate-200 text-slate-700 border-transparent',
    },
};

export const DOC_STATUS_FILTER_LABELS: Record<DocStatusFilter, string> = {
    all: 'All documents',
    missing: 'Missing',
    searching: 'Searching',
    'needs-review': 'Needs review',
    published: 'Published',
    'library-only': 'Library only',
    'search-failed': 'Search failed',
    'ingest-failed': 'Ingest failed',
    revisit: 'Revisit',
    parked: 'Parked',
    'brand-review': 'Needs brand review',
    archived: 'Archived',
};
